import applyCaseMiddleware from "axios-case-converter";
import axios from "axios";
import language from "../utils/language";
import cookies from "browser-cookies";
import * as urls from "../utils/urls";
import GeoCodingResult from "../models/GeoCodingResult";
import AutoCompleteResult from "../models/AutoCompleteResult";
import geoStorages from "../utils/SessionStorageRingBuffer";
import {roundFloat} from "../utils";
import RouteRequest from "../models/RouteRequest";
import RouteResponsePoint from "../models/RouteResponsePoint";

let lastVisualRouteClientAbortController = null;
let lastRouteInfoClientAbortController = null;

const getVisualRouteClientAborter = () => {
  if (lastVisualRouteClientAbortController && !lastVisualRouteClientAbortController.signal.aborted) {
    lastVisualRouteClientAbortController.abort();
  }
  lastVisualRouteClientAbortController = new AbortController();

  return lastVisualRouteClientAbortController;
};

const getRouteInfoClientAborter = () => {
  if (lastRouteInfoClientAbortController && !lastRouteInfoClientAbortController.signal.aborted) {
    lastRouteInfoClientAbortController.abort();
  }
  lastRouteInfoClientAbortController = new AbortController();

  return lastRouteInfoClientAbortController;
};

const createClient = () => axios.create({
  headers: {
    "Accept-Language": language.getLanguage(),
  },
});

const caseConverterClient = applyCaseMiddleware(createClient());

class Distancer {
  /**
   * @param params
   * @returns {Promise<RouteResponse>}
   */
  getVisualRoute = async params => {
    const response = await caseConverterClient({
      method: "POST",
      url: `${process.env.GEO_ROUTES_API_URL}?visual-route=true`,
      data: params,
      signal: getVisualRouteClientAborter().signal
    });
    return response.data; // NOTE: здесь происходит неявное приведение к RouteResponse
  };

  /**
   * @param params
   * @param {RouteRequest} routeRequest
   * @returns {Promise<RouteResponse>}
   */
  getRouteInfo = async (params, routeRequest) => {
    const request = {
      method: "POST",
      url: `${process.env.GEO_ROUTES_API_URL}?route-info=true&visual-route=true`,
      data: params,
      signal: getRouteInfoClientAborter().signal
    };

    const name = 'debug-info';
    const debugInfoCookieValue = cookies.get(name);
    if (debugInfoCookieValue) {
      request.headers = request.headers || {};
      request.headers[name] = debugInfoCookieValue;
    }

    const response = await caseConverterClient(request);
    /** @type {RouteResponse} */
    const routeResponse = response.data;
    const cityIds = routeResponse.routeInfo.segments.map(s => s.routePoint.cityInfo?.cityId);
    /** @type {City[]} */
    const cities = await geoStorages.cities.getByIds(cityIds);
    const citiesMap = new Map(cities.map(c => [c.id, c]));
    const keyPoints = RouteRequest.getAllRoutePoints(routeRequest);
    let keyPointIndex = 0;
    /** @type {RouteRequestPoint} */
    let routeRequestPoint;
    
    // Обогащаем информацию о городах.
    for (const segment of routeResponse.routeInfo.segments) {
      routeRequestPoint = RouteResponsePoint.isKeyPoint(segment.routePoint) ? keyPoints[keyPointIndex++] : undefined;
      
      // noinspection JSAccessibilityCheck
      if (segment.routePoint.cityInfo) {
        // noinspection JSAccessibilityCheck
        const city = citiesMap.get(segment.routePoint.cityInfo.cityId);
        segment.routePoint.city = city;
        segment.routePoint.region = city.region;
        segment.routePoint.country = city.country;
      }
      else {
        // случай, если города нет, есть координаты
        if(routeRequestPoint) {
          segment.routePoint.city = routeRequestPoint.city;
          segment.routePoint.region = routeRequestPoint.region;
          segment.routePoint.country = routeRequestPoint.country;
        }
      }
      
      // noinspection JSAccessibilityCheck
      segment.routePoint.cityInfo = undefined; // Больше нам cityInfo не нужен, чтобы не путаться.
      // noinspection JSAccessibilityCheck
      segment.routePoint.regionInfo = undefined; // Больше нам regionInfo не нужен, чтобы не путаться.
      // noinspection JSAccessibilityCheck
      segment.routePoint.countryInfo = undefined; // Больше нам countryInfo не нужен, чтобы не путаться.
    }
    return routeResponse;
  };
}

class Geo {
  /**
   * @param {string} query
   * @returns {Promise<AutoCompleteResult[]>} */
  getSuggestions = async (query) => {
    const response = await caseConverterClient({
      method: "POST",
      url: process.env.GEO_AUTOCOMPLETE_API_URL,
      data: {
        prefix: query,
        suggestion_types: 1,
        limit: 10
      }
    });

    const suggestions = response.data.suggestions;

    return suggestions.map(suggestion => {
      return new AutoCompleteResult({
        address: suggestion.address,
        cityId: suggestion.city.id,
        countryId: suggestion.country.id
      });
    });
  };

  getCities = ids => {
    const params = {ids: ids};
    return caseConverterClient({
      method: "POST",
      url: `${process.env.GEO_CITIES_API_URL}`,
      data: params
    });
  };

  getCityTypes = ids => {
    const params = {ids: ids};
    return caseConverterClient({
      method: "POST",
      url: `${process.env.GEO_CITYTYPES_API_URL}`,
      data: params
    });
  };

  getRegions = ids => {
    const params = {ids: ids};
    return caseConverterClient({
      method: "POST",
      url: `${process.env.GEO_REGIONS_API_URL}`,
      data: params
    });
  };

  getCountries = ids => {
    const params = {ids: ids};
    return caseConverterClient({
      method: "POST",
      url: `${process.env.GEO_COUNTRIES_API_URL}`,
      data: params
    });
  };

  /**
   * @param {GeoPoint[]} geoPoints
   * @param signal
   * @returns {Promise<GeoCodingResult>} */
  reverseGeocoding = async (geoPoints, signal = undefined) => {
    geoPoints = geoPoints.filter(Boolean);
    geoPoints.forEach(geoPoint => {
      geoPoint.lat = roundFloat(geoPoint.lat, 6);
      geoPoint.lon = roundFloat(geoPoint.lon, 6);
    });
    
    const params = {locations: geoPoints};
    try {
      const response = await caseConverterClient({
        method: "POST",
        url: `${process.env.GEO_REVERSE_GEOCODING_API_URL}`,
        data: params,
        signal: signal
      });

      /** @type {GeoCodingResult[]} */
      const result = [];
      const geoCodedItems = response.data;
      for (let i = 0; i < geoPoints.length; i++) {
        const geoCodedItem = geoCodedItems[i];
        const suggestion = geoCodedItem.suggestions[0];
        result.push(new GeoCodingResult({
          cityId: suggestion?.city?.id,
          regionId: suggestion?.region?.id,
          countryId: suggestion?.country?.id,
          // Гео-кодирование отдает координаты объекта, до которого получилось кодировать. Для регионов и стран вообще нет координат.
          // Для маршрута нам всегда интересны координаты, которые выбрал пользователь.
          geoPoint: geoPoints[i]
        }));
      }
      
      return result;
    }
    catch (e) {
      const isCancelled = axios.isCancel(e);
      if (isCancelled) {
        return [];
      }
      
      console.error(e);
      throw e;
    }
  };
}

class Stats {
  getCommonStats = () =>
    axios({
      method: "GET",
      url: urls.getMainUrl(process.env.COUNTERS_API_URL),
      withCredentials: true,
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
    });
}

const getPricelineBaseUrl = () =>
  process.env.PRICELINE_API_URL.startsWith("http")
    ? process.env.PRICELINE_API_URL
    : urls.getMainUrl(process.env.PRICELINE_API_URL);

class Priceline {
  getLastRates = async ({ fromPoint, toPoint, carType, tonnage = 5, nds = true }) => {
    const response = await caseConverterClient({
      method: "GET",
      url: `${getPricelineBaseUrl()}/v1/site/last-rates`,
      params: {
        from_point: fromPoint,
        to_point: toPoint,
        car_type: carType,
        tonnage,
        nds,
      },
      withCredentials: true,
    });
    return response.data;
  };

  getSimilarDirections = async ({ fromPoint, toPoint, carTypes, tonnage = 5 }) => {
    const response = await caseConverterClient({
      method: "GET",
      url: `${getPricelineBaseUrl()}/site/directions/similar`,
      params: {
        from_point: fromPoint,
        to_point: toPoint,
        car_types: Array.isArray(carTypes) ? carTypes.join(",") : carTypes,
        tonnage,
      },
      withCredentials: true,
    });
    return response.data;
  };
}

class API {
  constructor() {
    this.geo = new Geo();
    this.distancer = new Distancer();
    this.stats = new Stats();
    this.priceline = new Priceline();
  }
}

const api = new API();

export default api;
